/**
 * Hooks platform-admin del Estudio Contable — TanStack Query.
 *
 * Consumen los endpoints `/firm/admin/*` (gate `IsPlatformAdmin`): crear/editar
 * estudios, ver el detalle (cartera + staff + solicitudes), vincular/desvincular
 * empresas, gestionar miembros, resolver solicitudes y buscar usuarios. Es la UI
 * que Zenlatam usa para dar de alta un estudio sin pasar por el shell (#105).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import apiClient from '@/lib/apiClient'
import type {
  AccountingFirm, FirmClient, FirmClientRequest, FirmRole, FirmStaffMember,
} from './useFirm'

// ─── Tipos ──────────────────────────────────────────────────────────────────

export interface PlatformFirm extends AccountingFirm {
  clients_count: number | null
  staff_count: number | null
}

export interface FirmAdminDetail {
  firm: AccountingFirm
  clients: FirmClient[]
  staff: FirmStaffMember[]
  requests: FirmClientRequest[]
}

export interface AdminUserResult {
  id: string
  email: string
  full_name: string
  is_platform_admin: boolean
}

// ─── Keys ─────────────────────────────────────────────────────────────────────
export const firmAdminKeys = {
  firms: ['firm-admin', 'firms'] as const,
  firm: (id: string) => ['firm-admin', 'firm', id] as const,
  requests: (status?: string) => ['firm-admin', 'requests', status ?? 'all'] as const,
  users: (q: string) => ['firm-admin', 'users', q] as const,
}

// ─── Queries ──────────────────────────────────────────────────────────────────

export function usePlatformFirms() {
  return useQuery({
    queryKey: firmAdminKeys.firms,
    queryFn: async (): Promise<PlatformFirm[]> => {
      const { data } = await apiClient.get<{ firms: PlatformFirm[]; count: number }>('/firm/admin/firms/')
      return data.firms
    },
  })
}

export function useFirmAdminDetail(firmId: string | null) {
  return useQuery({
    queryKey: firmId ? firmAdminKeys.firm(firmId) : ['firm-admin', 'firm', 'none'],
    enabled: !!firmId,
    queryFn: async (): Promise<FirmAdminDetail> => {
      const { data } = await apiClient.get<FirmAdminDetail>(`/firm/admin/firms/${firmId}/`)
      return data
    },
  })
}

export function usePlatformRequests(status?: 'pending' | 'approved' | 'rejected') {
  return useQuery({
    queryKey: firmAdminKeys.requests(status),
    queryFn: async (): Promise<FirmClientRequest[]> => {
      const { data } = await apiClient.get<{ requests: FirmClientRequest[]; count: number }>(
        '/firm/admin/requests/', { params: status ? { status } : undefined },
      )
      return data.requests
    },
  })
}

export function useSearchAdminUsers(q: string) {
  return useQuery({
    queryKey: firmAdminKeys.users(q),
    enabled: q.trim().length >= 2,
    queryFn: async (): Promise<AdminUserResult[]> => {
      const { data } = await apiClient.get<{ users: AdminUserResult[] }>(
        '/firm/admin/users/', { params: { q } },
      )
      return data.users
    },
  })
}

// ─── Mutations ──────────────────────────────────────────────────────────────────

function useInvalidateAll() {
  const qc = useQueryClient()
  return () => {
    qc.invalidateQueries({ queryKey: firmAdminKeys.firms })
    qc.invalidateQueries({ queryKey: ['firm-admin', 'firm'] })
    qc.invalidateQueries({ queryKey: ['firm-admin', 'requests'] })
  }
}

export function useCreateFirm() {
  const invalidate = useInvalidateAll()
  return useMutation({
    mutationFn: async (payload: {
      name: string; tax_id?: string; billing_email?: string; admin_email?: string
    }): Promise<AccountingFirm> => {
      const { data } = await apiClient.post<AccountingFirm>('/firm/admin/firms/', payload)
      return data
    },
    onSuccess: invalidate,
  })
}

export function useUpdateFirm() {
  const invalidate = useInvalidateAll()
  return useMutation({
    mutationFn: async ({ id, payload }: {
      id: string
      payload: Partial<Pick<AccountingFirm, 'name' | 'tax_id' | 'billing_email' | 'status' | 'plan'>>
    }): Promise<AccountingFirm> => {
      const { data } = await apiClient.patch<AccountingFirm>(`/firm/admin/firms/${id}/`, payload)
      return data
    },
    onSuccess: invalidate,
  })
}

export function useLinkFirmClient() {
  const invalidate = useInvalidateAll()
  return useMutation({
    mutationFn: async (payload: {
      firm_id: string; tenant_schema: string; tenant_name?: string; is_own_company?: boolean
    }): Promise<FirmClient> => {
      const { data } = await apiClient.post<FirmClient>('/firm/admin/clients/', payload)
      return data
    },
    onSuccess: invalidate,
  })
}

export function useUpdateFirmClientAdmin() {
  const invalidate = useInvalidateAll()
  return useMutation({
    mutationFn: async ({ id, payload }: {
      id: string
      payload: { is_active?: boolean; is_own_company?: boolean; billable?: boolean }
    }): Promise<FirmClient> => {
      const { data } = await apiClient.patch<FirmClient>(`/firm/admin/clients/${id}/`, payload)
      return data
    },
    onSuccess: invalidate,
  })
}

export function useUnlinkFirmClient() {
  const invalidate = useInvalidateAll()
  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      await apiClient.delete(`/firm/admin/clients/${id}/`)
    },
    onSuccess: invalidate,
  })
}

export function useAddFirmMember() {
  const invalidate = useInvalidateAll()
  return useMutation({
    mutationFn: async ({ firmId, payload }: {
      firmId: string
      payload: { email?: string; user_id?: string; firm_role: FirmRole }
    }): Promise<FirmStaffMember> => {
      const { data } = await apiClient.post<FirmStaffMember>(`/firm/admin/firms/${firmId}/members/`, payload)
      return data
    },
    onSuccess: invalidate,
  })
}

export function useUpdateFirmMember() {
  const invalidate = useInvalidateAll()
  return useMutation({
    mutationFn: async ({ id, payload }: {
      id: string
      payload: { firm_role?: FirmRole; status?: 'pending' | 'accepted' | 'revoked' }
    }): Promise<FirmStaffMember> => {
      const { data } = await apiClient.patch<FirmStaffMember>(`/firm/admin/members/${id}/`, payload)
      return data
    },
    onSuccess: invalidate,
  })
}

export function useResolveRequest() {
  const invalidate = useInvalidateAll()
  return useMutation({
    mutationFn: async ({ id, payload }: {
      id: string
      payload: {
        action: 'approve' | 'reject'
        tenant_schema?: string
        tenant_name?: string
      }
    }): Promise<FirmClientRequest> => {
      const { data } = await apiClient.post<FirmClientRequest>(
        `/firm/admin/requests/${id}/resolve/`, payload,
      )
      return data
    },
    onSuccess: invalidate,
  })
}
